export const DEFAULT_EXPOSURE_GRADE_THRESHOLDS = {
  exposureToRunRate: { grade3: 40, grade2: 20, grade1: 10 },
  exposureToRunOff: { grade3: 80, grade2: 50, grade1: 25 },
};

/** Maps UW scorecard overall grade to tearsheet score band (3 = A, 2 = B, 1 = C). */
export function overallGradeToScoreBand(overallGrade) {
  switch (overallGrade) {
    case 'HIGH CONVICTION':
      return 3;
    case 'MODERATE':
      return 2;
    case 'LOW / PASS':
      return 1;
    default:
      return null;
  }
}

export function overallGradeToLetterGrade(overallGrade) {
  const band = overallGradeToScoreBand(overallGrade);
  if (band === 3) {
    return 'A';
  }
  if (band === 2) {
    return 'B';
  }
  if (band === 1) {
    return 'C';
  }
  return null;
}

export function formatOverallGradeDisplay(overallGrade) {
  switch (overallGrade) {
    case 'HIGH CONVICTION':
      return 'High Conviction — A';
    case 'MODERATE':
      return 'Moderate — B';
    case 'LOW / PASS':
      return 'Low — C';
    case 'Enter Ratings':
      return 'Enter Ratings';
    default:
      return overallGrade || '—';
  }
}

export function normalizeGradeThresholds(gradeThresholds) {
  const raw = gradeThresholds ?? DEFAULT_EXPOSURE_GRADE_THRESHOLDS;
  return {
    exposureToRunRate: {
      grade3:
        Number(raw.exposureToRunRate?.grade3) ||
        DEFAULT_EXPOSURE_GRADE_THRESHOLDS.exposureToRunRate.grade3,
      grade2:
        Number(raw.exposureToRunRate?.grade2) ||
        DEFAULT_EXPOSURE_GRADE_THRESHOLDS.exposureToRunRate.grade2,
      grade1:
        Number(raw.exposureToRunRate?.grade1) ||
        DEFAULT_EXPOSURE_GRADE_THRESHOLDS.exposureToRunRate.grade1,
    },
    exposureToRunOff: {
      grade3:
        Number(raw.exposureToRunOff?.grade3) ||
        DEFAULT_EXPOSURE_GRADE_THRESHOLDS.exposureToRunOff.grade3,
      grade2:
        Number(raw.exposureToRunOff?.grade2) ||
        DEFAULT_EXPOSURE_GRADE_THRESHOLDS.exposureToRunOff.grade2,
      grade1:
        Number(raw.exposureToRunOff?.grade1) ||
        DEFAULT_EXPOSURE_GRADE_THRESHOLDS.exposureToRunOff.grade1,
    },
  };
}

export function resolveExposureLimitsForScoreBand(scoreBand, gradeThresholds) {
  if (scoreBand == null || scoreBand < 1 || scoreBand > 3) {
    return null;
  }
  const normalized = normalizeGradeThresholds(gradeThresholds);
  const gradeKey = `grade${scoreBand}`;
  return {
    runRateMaxPct: normalized.exposureToRunRate[gradeKey],
    runoffMaxPct: normalized.exposureToRunOff[gradeKey],
  };
}

export function resolveExposureLimitsForOverallGrade(overallGrade, gradeThresholds) {
  return resolveExposureLimitsForScoreBand(
    overallGradeToScoreBand(overallGrade),
    gradeThresholds
  );
}

export function isExposureOverLimit(exposurePct, maxPct) {
  if (exposurePct == null || !Number.isFinite(Number(exposurePct))) {
    return false;
  }
  if (maxPct == null || !Number.isFinite(Number(maxPct))) {
    return false;
  }
  return Number(exposurePct) > Number(maxPct);
}

export function formatExposureLimitsLine(overallGrade, gradeThresholds) {
  const limits = resolveExposureLimitsForOverallGrade(overallGrade, gradeThresholds);
  if (!limits) {
    return null;
  }
  return `Run rate ≤ ${limits.runRateMaxPct}%, runoff ≤ ${limits.runoffMaxPct}%`;
}
